<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Check, ExternalLink, Eye, History, Pencil, Plus, RotateCcw, Search, X } from '@lucide/vue'
import {
  Badge, Button, ConfirmAction, EmptyState, FormField, Input, Modal,
  Pagination, Section, Select, Spinner, Table, Toolbar, useToast,
} from '@drama/ui'

import {
  getTalkingVideoFacets,
  getTalkingVideoHistory,
  listTalkingVideoSpecs,
  listTalkingVideos,
  reviewTalkingVideoPrompt,
  saveTalkingVideo,
  withdrawTalkingVideo,
} from '../../api/asset-library.js'
import { listVideoPreparation } from '../../api/index.js'
import ScenarioPicker from '../../components/ScenarioPicker.vue'
import { formatDate } from '../intent-video/format.js'

const { locale, t } = useI18n()
const router = useRouter()
const toast = useToast()
const props = defineProps({ initialPackId: { type: String, default: '' } })

const loading = ref(false)
const error = ref('')
const items = ref([])
const scenarioItems = ref([])
const facets = reactive({ packs: [], entities: [] })
const total = ref(0)
const page = ref(1)
const pages = ref(1)
const pageSize = ref(20)
const filters = reactive({
  q: '',
  pack_id: props.initialPackId,
  entity_type: '',
  quality: '',
  status: '',
  sort: 'updated_at',
  order: 'desc',
})
const specLoading = ref(false)
const specError = ref('')
const specItems = ref([])
const specTotal = ref(0)
const specPage = ref(1)
const specPages = ref(1)
const specPageSize = ref(20)
const reviewingPromptId = ref('')
const promptDetailOpen = ref(false)
const promptDetail = ref(null)

const specGroups = computed(() => {
  const groups = new Map()
  for (const item of specItems.value) {
    const groupKey = `${item.pack_id}:${item.entity_id}`
    const group = groups.get(groupKey)
    if (group === undefined) {
      groups.set(groupKey, {
        key: groupKey,
        entity_id: item.entity_id,
        entity_name: item.entity_name,
        entity_type: item.entity_type,
        identity_reference: item.identity_reference,
        items: [item],
      })
    } else {
      group.items.push(item)
    }
  }
  return [...groups.values()]
})

const columns = computed(() => [
  { key: 'entity', label: t('visualGameAdmin.assetLibrary.talkingVideos.actor'), width: '180px' },
  { key: 'scope', label: t('visualGameAdmin.assetLibrary.talkingVideos.scope'), width: '200px' },
  { key: 'video', label: t('visualGameAdmin.assetLibrary.talkingVideos.video'), width: '230px' },
  { key: 'frames', label: t('visualGameAdmin.assetLibrary.talkingVideos.frames'), width: '180px' },
  { key: 'status', label: t('visualGameAdmin.assetLibrary.talkingVideos.status'), width: '100px' },
  { key: 'updated_at', label: t('visualGameAdmin.assetLibrary.table.updatedAt'), width: '170px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '250px', align: 'right', fixed: 'right' },
])

const entityTypeOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.assetLibrary.talkingVideos.allActors') },
  { value: 'player', label: t('visualGameAdmin.entityLibrary.type.player') },
  { value: 'npc', label: t('visualGameAdmin.entityLibrary.type.npc') },
])
const qualityOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.assetLibrary.talkingVideos.allPromptQualities') },
  { value: 'draft', label: t('visualGameAdmin.assetLibrary.talkingVideos.promptQuality.draft') },
  { value: 'approved', label: t('visualGameAdmin.assetLibrary.talkingVideos.promptQuality.approved') },
  { value: 'rejected', label: t('visualGameAdmin.assetLibrary.talkingVideos.promptQuality.rejected') },
])
const statusOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.assetLibrary.talkingVideos.allStatuses') },
  { value: 'published', label: t('visualGameAdmin.assetLibrary.talkingVideos.published') },
  { value: 'withdrawn', label: t('visualGameAdmin.assetLibrary.talkingVideos.withdrawn') },
])
const sortOptions = computed(() => [
  { value: 'updated_at', label: t('visualGameAdmin.assetLibrary.talkingVideos.sortUpdated') },
  { value: 'entity_name', label: t('visualGameAdmin.assetLibrary.talkingVideos.sortActor') },
  { value: 'pack_title', label: t('visualGameAdmin.assetLibrary.talkingVideos.sortPack') },
])
const orderOptions = computed(() => [
  { value: 'desc', label: t('visualGameAdmin.assetLibrary.talkingVideos.desc') },
  { value: 'asc', label: t('visualGameAdmin.assetLibrary.talkingVideos.asc') },
])
const entityOptions = computed(() => facets.entities.map(entity => ({
  value: entity.entity_id,
  label: `${entity.entity_name} · ${t(`visualGameAdmin.entityLibrary.type.${entity.entity_type}`)}`,
})))
const formPackOptions = computed(() => facets.packs.map(pack => ({ value: pack.pack_id, label: pack.pack_title })))
const summary = computed(() => t('visualGameAdmin.assetLibrary.pager.summary', {
  total: total.value,
  page: page.value,
  pages: pages.value,
}))
const specSummary = computed(() => t('visualGameAdmin.assetLibrary.pager.summary', {
  total: specTotal.value,
  page: specPage.value,
  pages: specPages.value,
}))

onMounted(initialize)

async function initialize() {
  loading.value = true
  error.value = ''
  try {
    const [facetData, preparationData] = await Promise.all([
      getTalkingVideoFacets(),
      listVideoPreparation(locale.value),
    ])
    facets.packs = facetData.packs
    facets.entities = facetData.entities
    scenarioItems.value = preparationData.items
  } catch (requestError) {
    error.value = requestError.message
    loading.value = false
    return
  }
  await Promise.all([load(), loadSpecs()])
}

async function loadSpecs() {
  specLoading.value = true
  specError.value = ''
  try {
    const data = await listTalkingVideoSpecs({
      q: filters.q.trim() === '' ? undefined : filters.q.trim(),
      pack_id: filters.pack_id === '' ? undefined : filters.pack_id,
      entity_type: filters.entity_type === '' ? undefined : filters.entity_type,
      quality: filters.quality === '' ? undefined : filters.quality,
      page: specPage.value,
      limit: specPageSize.value,
    })
    specItems.value = data.items
    specTotal.value = data.total
    specPage.value = data.page
    specPages.value = data.pages
  } catch (requestError) {
    specError.value = requestError.message
  } finally {
    specLoading.value = false
  }
}

function openPromptDetail(item) {
  promptDetail.value = item
  promptDetailOpen.value = true
}

async function reviewPrompt(item, quality) {
  reviewingPromptId.value = item.prompt.id
  try {
    const response = await reviewTalkingVideoPrompt(item.entity_id, item.expression_state, {
      pack_id: item.pack_id,
      quality,
    })
    const index = specItems.value.findIndex(spec => spec.id === response.item.id)
    specItems.value[index] = response.item
    if (promptDetail.value !== null && promptDetail.value.id === response.item.id) promptDetail.value = response.item
    toast.success(t('visualGameAdmin.assetLibrary.talkingVideos.promptReviewed'))
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    reviewingPromptId.value = ''
  }
}

function promptQualityVariant(quality) {
  if (quality === 'approved') return 'success'
  if (quality === 'rejected') return 'danger'
  return 'neutral'
}

function h3ParamsText(item) {
  const params = item.prompt.params.h3
  return `${params.width}×${params.height} · ${params.frames}f · ${params.steps} steps · turbo ${params.turbo}`
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const data = await listTalkingVideos({
      q: filters.q.trim() === '' ? undefined : filters.q.trim(),
      pack_id: filters.pack_id === '' ? undefined : filters.pack_id,
      entity_type: filters.entity_type === '' ? undefined : filters.entity_type,
      status: filters.status === '' ? undefined : filters.status,
      sort: filters.sort,
      order: filters.order,
      page: page.value,
      limit: pageSize.value,
    })
    items.value = data.items
    total.value = data.total
    page.value = data.page
    pages.value = data.pages
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

async function applyFilters() {
  page.value = 1
  specPage.value = 1
  await Promise.all([load(), loadSpecs()])
}

function resetFilters() {
  Object.assign(filters, {
    q: '',
    pack_id: '',
    entity_type: '',
    quality: '',
    status: '',
    sort: 'updated_at',
    order: 'desc',
  })
  applyFilters()
}

function changePageSize(size) {
  pageSize.value = size
  applyFilters()
}

function prevPage() {
  page.value -= 1
  load()
}

function nextPage() {
  page.value += 1
  load()
}

function changeSpecPageSize(size) {
  specPageSize.value = size
  specPage.value = 1
  loadSpecs()
}

function prevSpecPage() {
  specPage.value -= 1
  loadSpecs()
}

function nextSpecPage() {
  specPage.value += 1
  loadSpecs()
}

function openMediaAsset(mediaAssetId) {
  router.push({ name: 'media-asset-detail', params: { id: mediaAssetId } })
}

const formOpen = ref(false)
const saving = ref(false)
const editingItem = ref(null)
const form = reactive({
  entity_id: '',
  pack_id: '',
  expression_state: '',
  video_asset_id: '',
  start_frame_asset_id: '',
  end_frame_asset_id: '',
})
const isEditing = computed(() => editingItem.value !== null)
const formValid = computed(() => Object.values(form).every(value => value.trim() !== ''))

function openCreate(item) {
  editingItem.value = null
  Object.assign(form, {
    entity_id: item.entity_id,
    pack_id: item.pack_id,
    expression_state: item.expression_state,
    video_asset_id: '',
    start_frame_asset_id: '',
    end_frame_asset_id: '',
  })
  formOpen.value = true
}

function openEdit(item) {
  editingItem.value = item
  Object.assign(form, {
    entity_id: item.entity_id,
    pack_id: item.pack_id,
    expression_state: item.expression_state,
    video_asset_id: item.video.media_asset_id,
    start_frame_asset_id: item.start_frame.media_asset_id,
    end_frame_asset_id: item.end_frame.media_asset_id,
  })
  formOpen.value = true
}

async function submitForm() {
  saving.value = true
  try {
    await saveTalkingVideo(form.entity_id, form.expression_state, {
      pack_id: form.pack_id,
      video_asset_id: form.video_asset_id,
      start_frame_asset_id: form.start_frame_asset_id,
      end_frame_asset_id: form.end_frame_asset_id,
    })
    formOpen.value = false
    toast.success(t('visualGameAdmin.assetLibrary.talkingVideos.saved'))
    await load()
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}

async function withdraw(item) {
  await withdrawTalkingVideo(item.entity_id, item.expression_state, { pack_id: item.pack_id })
}

async function onWithdrawn() {
  toast.success(t('visualGameAdmin.assetLibrary.talkingVideos.withdrawnNotice'))
  await load()
}

function onActionError(requestError) {
  toast.error(requestError.message, { duration: 0 })
}

const historyOpen = ref(false)
const historyLoading = ref(false)
const historyItems = ref([])
const historyIdentity = ref('')

async function openHistory(item) {
  historyOpen.value = true
  historyLoading.value = true
  historyIdentity.value = `${item.entity_name} · ${item.pack_title} · ${item.expression_state}`
  try {
    const data = await getTalkingVideoHistory(item.entity_id, item.expression_state, item.pack_id)
    historyItems.value = data.items
  } catch (requestError) {
    historyOpen.value = false
    toast.error(requestError.message, { duration: 0 })
  } finally {
    historyLoading.value = false
  }
}

function auditMediaId(snapshot) {
  return snapshot === null ? '—' : snapshot.media_asset_id
}
</script>

<template>
  <div class="tvt" data-insp-path="apps/visual-game-admin/src/pages/asset-library/TalkingVideosTab.vue">
    <Section>
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.assetLibrary.filters.search') }}</label>
          <Input v-model="filters.q" type="search" size="sm" :placeholder="t('visualGameAdmin.assetLibrary.talkingVideos.searchPlaceholder')" @keyup.enter="applyFilters" />
        </div>
        <div class="drama-toolbar-field tvt__scenario-filter">
          <label>{{ t('visualGameAdmin.assetLibrary.talkingVideos.pack') }}</label>
          <ScenarioPicker
            v-model="filters.pack_id"
            :items="scenarioItems"
            allow-empty
            :empty-label="t('visualGameAdmin.assetLibrary.talkingVideos.allPacks')"
            :search-placeholder="t('visualGameAdmin.scenarioPicker.searchPlaceholder')"
            :empty-result="t('visualGameAdmin.scenarioPicker.emptyResult')"
          />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.assetLibrary.talkingVideos.actorType') }}</label>
          <Select v-model="filters.entity_type" :options="entityTypeOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.assetLibrary.talkingVideos.promptQualityLabel') }}</label>
          <Select v-model="filters.quality" :options="qualityOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.assetLibrary.talkingVideos.status') }}</label>
          <Select v-model="filters.status" :options="statusOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.assetLibrary.talkingVideos.sort') }}</label>
          <Select v-model="filters.sort" :options="sortOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.assetLibrary.talkingVideos.order') }}</label>
          <Select v-model="filters.order" :options="orderOptions" size="sm" />
        </div>
        <template #actions>
          <Button size="sm" :loading="loading || specLoading" @click="applyFilters"><Search :size="14" />{{ t('visualGameAdmin.assetLibrary.filters.apply') }}</Button>
          <Button variant="ghost" size="sm" :disabled="loading || specLoading" @click="resetFilters"><RotateCcw :size="14" />{{ t('visualGameAdmin.common.reset') }}</Button>
        </template>
      </Toolbar>
    </Section>

    <Section :title="t('visualGameAdmin.assetLibrary.talkingVideos.specReviewTitle')">
      <p class="tvt__section-description">{{ t('visualGameAdmin.assetLibrary.talkingVideos.specReviewDescription') }}</p>
      <div v-if="specLoading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
      <div v-else-if="specError" class="vga-state">
        <p class="vga-error">{{ specError }}</p>
        <Button variant="secondary" @click="loadSpecs">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
      <EmptyState v-else-if="specItems.length === 0" :title="t('visualGameAdmin.assetLibrary.talkingVideos.noSpecs')" />
      <div v-else class="tvt__spec-groups">
        <section v-for="group in specGroups" :key="group.key" class="tvt__spec-group">
          <header class="tvt__spec-group-header">
            <div>
              <h3>{{ group.entity_name }}</h3>
              <span>{{ t(`visualGameAdmin.entityLibrary.type.${group.entity_type}`) }} · {{ group.entity_id }}</span>
            </div>
            <Badge size="sm">{{ t('visualGameAdmin.assetLibrary.talkingVideos.stateCount', { count: group.items.length }) }}</Badge>
          </header>
          <div v-if="group.identity_reference" class="tvt__identity-reference">
            <button type="button" @click="openMediaAsset(group.identity_reference.media_asset_id)">
              <img :src="group.identity_reference.public_url" :alt="t('visualGameAdmin.assetLibrary.talkingVideos.identityReference')" />
            </button>
            <div>
              <strong>{{ t('visualGameAdmin.assetLibrary.talkingVideos.identityReference') }}</strong>
              <p>{{ t('visualGameAdmin.assetLibrary.talkingVideos.identityReferenceDescription') }}</p>
              <code>{{ group.identity_reference.media_asset_id }}</code>
            </div>
          </div>
          <div class="tvt__spec-grid">
            <article v-for="item in group.items" :key="item.id" class="tvt__spec-card">
              <header class="tvt__spec-card-header">
                <div>
                  <strong>{{ item.prompt.params.performance_state.display_name }}</strong>
                  <code>{{ item.expression_state }}</code>
                </div>
                <Badge size="sm" :variant="promptQualityVariant(item.prompt.quality)">
                  {{ t(`visualGameAdmin.assetLibrary.talkingVideos.promptQuality.${item.prompt.quality}`) }}
                </Badge>
              </header>
              <p>{{ item.prompt.params.performance_state.design_goal }}</p>
              <ul class="tvt__conditions">
                <li v-for="condition in item.prompt.params.performance_state.selection_conditions" :key="condition">{{ condition }}</li>
              </ul>
              <div class="tvt__phases">
                <span>{{ t('visualGameAdmin.assetLibrary.talkingVideos.availablePhases') }}</span>
                <Badge
                  v-for="phase in item.prompt.params.performance_state.available_phases"
                  :key="phase"
                  size="sm"
                >{{ phase }}</Badge>
              </div>
              <button v-if="item.state_frame" type="button" class="tvt__state-frame" @click="openMediaAsset(item.state_frame.media_asset_id)">
                <img :src="item.state_frame.public_url" :alt="item.prompt.params.performance_state.display_name" />
                <span>{{ t('visualGameAdmin.assetLibrary.talkingVideos.stateFrame') }} <ExternalLink :size="13" /></span>
              </button>
              <div v-else class="tvt__references">
                <figure v-for="reference in item.prompt.params.references" :key="reference.id">
                  <img :src="reference.public_url" :alt="reference.role" />
                  <figcaption>{{ reference.role }}</figcaption>
                </figure>
              </div>
              <div class="tvt__spec-video">
                <video v-if="item.video" :src="item.video.public_url" controls preload="metadata" playsinline />
                <div v-else class="tvt__spec-video-empty">{{ t('visualGameAdmin.assetLibrary.talkingVideos.videoPreviewEmpty') }}</div>
              </div>
              <div class="tvt__spec-meta">
                <span>{{ h3ParamsText(item) }}</span>
                <Badge v-if="item.video" size="sm" variant="success">{{ t('visualGameAdmin.assetLibrary.talkingVideos.videoReady') }}</Badge>
                <Badge v-else size="sm" variant="neutral">{{ t('visualGameAdmin.assetLibrary.talkingVideos.videoPending') }}</Badge>
              </div>
              <footer class="tvt__spec-actions">
                <Button variant="ghost" size="sm" @click="openPromptDetail(item)"><Eye :size="14" />{{ t('visualGameAdmin.assetLibrary.talkingVideos.reviewPrompt') }}</Button>
                <Button
                  v-if="item.video === null && item.prompt.quality === 'approved'"
                  variant="ghost"
                  size="sm"
                  @click="openCreate(item)"
                ><Plus :size="14" />{{ t('visualGameAdmin.assetLibrary.talkingVideos.bindVideo') }}</Button>
                <Button
                  variant="ghost" size="sm" :disabled="item.prompt.quality === 'approved'"
                  :loading="reviewingPromptId === item.prompt.id" @click="reviewPrompt(item, 'approved')"
                ><Check :size="14" />{{ t('visualGameAdmin.assetLibrary.talkingVideos.approvePrompt') }}</Button>
                <Button
                  variant="ghost" size="sm" :disabled="item.prompt.quality === 'rejected'"
                  :loading="reviewingPromptId === item.prompt.id" @click="reviewPrompt(item, 'rejected')"
                ><X :size="14" />{{ t('visualGameAdmin.assetLibrary.talkingVideos.rejectPrompt') }}</Button>
              </footer>
            </article>
          </div>
        </section>
      </div>
      <Pagination
        v-if="specItems.length > 0"
        :has-prev="specPage > 1" :has-next="specPage < specPages" :loading="specLoading" :summary="specSummary"
        :prev-label="t('visualGameAdmin.assetLibrary.pager.prev')" :next-label="t('visualGameAdmin.assetLibrary.pager.next')"
        :page-size="specPageSize" :page-size-options="[20, 50, 100]" :page-size-label="t('visualGameAdmin.assetLibrary.pager.pageSize')"
        @prev="prevSpecPage" @next="nextSpecPage" @update:page-size="changeSpecPageSize"
      />
    </Section>

    <Section :title="t('visualGameAdmin.assetLibrary.talkingVideos.generatedTitle')">
      <p class="tvt__section-description">{{ t('visualGameAdmin.assetLibrary.talkingVideos.generatedDescription') }}</p>
    </Section>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="initialize">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else>
      <Section :padded="false">
        <EmptyState v-if="items.length === 0" :title="t('visualGameAdmin.assetLibrary.talkingVideos.emptyTitle')" :description="t('visualGameAdmin.assetLibrary.talkingVideos.emptyDescription')" />
        <Table v-else :columns="columns" :rows="items">
          <template #entity="{ row }">
            <div class="tvt__stack"><strong>{{ row.entity_name }}</strong><span>{{ t(`visualGameAdmin.entityLibrary.type.${row.entity_type}`) }}</span></div>
          </template>
          <template #scope="{ row }">
            <div class="tvt__stack"><strong>{{ row.pack_title }}</strong><code>{{ row.expression_state }}</code></div>
          </template>
          <template #video="{ row }">
            <video :src="row.video.public_url" controls preload="metadata" class="tvt__video" />
            <button type="button" class="tvt__asset-link" @click="openMediaAsset(row.video.media_asset_id)"><ExternalLink :size="13" />{{ row.video.media_asset_id }}</button>
          </template>
          <template #frames="{ row }">
            <div class="tvt__frames">
              <button type="button" @click="openMediaAsset(row.start_frame.media_asset_id)"><img :src="row.start_frame.public_url" :alt="t('visualGameAdmin.assetLibrary.talkingVideos.startFrame')" /></button>
              <button type="button" @click="openMediaAsset(row.end_frame.media_asset_id)"><img :src="row.end_frame.public_url" :alt="t('visualGameAdmin.assetLibrary.talkingVideos.endFrame')" /></button>
            </div>
          </template>
          <template #status="{ row }"><Badge size="sm" :variant="row.status === 'published' ? 'success' : 'neutral'">{{ t(`visualGameAdmin.assetLibrary.talkingVideos.${row.status}`) }}</Badge></template>
          <template #updated_at="{ row }">{{ formatDate(row.updated_at) }}</template>
          <template #actions="{ row }">
            <div class="tvt__actions">
              <Button variant="ghost" size="sm" @click="openHistory(row)"><History :size="14" />{{ t('visualGameAdmin.assetLibrary.talkingVideos.history') }}</Button>
              <Button variant="ghost" size="sm" @click="openEdit(row)"><Pencil :size="14" />{{ row.status === 'withdrawn' ? t('visualGameAdmin.assetLibrary.talkingVideos.restore') : t('visualGameAdmin.common.edit') }}</Button>
              <ConfirmAction
                v-if="row.status === 'published'"
                :label="t('visualGameAdmin.assetLibrary.talkingVideos.withdraw')"
                :confirm-text="t('visualGameAdmin.assetLibrary.talkingVideos.withdrawConfirm', { name: row.entity_name, expression: row.expression_state })"
                :cancel-label="t('visualGameAdmin.common.cancel')"
                :ok-label="t('visualGameAdmin.assetLibrary.talkingVideos.withdraw')"
                :pending-label="t('visualGameAdmin.common.working')"
                danger
                :action="() => withdraw(row)"
                @done="onWithdrawn"
                @error="onActionError"
              />
            </div>
          </template>
        </Table>
      </Section>
      <Pagination
        :has-prev="page > 1" :has-next="page < pages" :loading="loading" :summary="summary"
        :prev-label="t('visualGameAdmin.assetLibrary.pager.prev')" :next-label="t('visualGameAdmin.assetLibrary.pager.next')"
        :page-size="pageSize" :page-size-options="[20, 50, 100]" :page-size-label="t('visualGameAdmin.assetLibrary.pager.pageSize')"
        @prev="prevPage" @next="nextPage" @update:page-size="changePageSize"
      />
    </template>

    <Modal :open="formOpen" :title="isEditing ? t('visualGameAdmin.assetLibrary.talkingVideos.editTitle') : t('visualGameAdmin.assetLibrary.talkingVideos.createTitle')" :close-on-backdrop="!saving" width="640px" @update:open="formOpen = $event">
      <form class="tvt__form" @submit.prevent="submitForm">
        <FormField :label="t('visualGameAdmin.assetLibrary.talkingVideos.actor')" required>
          <Select v-model="form.entity_id" :options="entityOptions" disabled block />
        </FormField>
        <FormField :label="t('visualGameAdmin.assetLibrary.talkingVideos.pack')" required>
          <Select v-model="form.pack_id" :options="formPackOptions" disabled block />
        </FormField>
        <FormField :label="t('visualGameAdmin.assetLibrary.talkingVideos.expression')" :hint="t('visualGameAdmin.assetLibrary.talkingVideos.expressionHint')" required>
          <Input v-model="form.expression_state" disabled pattern="[a-z][a-z0-9_]{0,63}" block required />
        </FormField>
        <FormField :label="t('visualGameAdmin.assetLibrary.talkingVideos.videoAssetId')" :hint="t('visualGameAdmin.assetLibrary.talkingVideos.silentHint')" required>
          <Input v-model="form.video_asset_id" block required />
        </FormField>
        <FormField :label="t('visualGameAdmin.assetLibrary.talkingVideos.startFrameAssetId')" required>
          <Input v-model="form.start_frame_asset_id" block required />
        </FormField>
        <FormField :label="t('visualGameAdmin.assetLibrary.talkingVideos.endFrameAssetId')" required>
          <Input v-model="form.end_frame_asset_id" block required />
        </FormField>
      </form>
      <template #footer>
        <Button variant="ghost" :disabled="saving" @click="formOpen = false">{{ t('visualGameAdmin.common.cancel') }}</Button>
        <Button variant="primary" :loading="saving" :disabled="!formValid" @click="submitForm">{{ t('visualGameAdmin.common.save') }}</Button>
      </template>
    </Modal>

    <Modal :open="historyOpen" :title="t('visualGameAdmin.assetLibrary.talkingVideos.historyTitle')" width="760px" @update:open="historyOpen = $event">
      <p class="tvt__history-identity">{{ historyIdentity }}</p>
      <div v-if="historyLoading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
      <div v-else class="tvt__history-list">
        <article v-for="entry in historyItems" :key="entry.id" class="tvt__history-item">
          <div><strong>{{ t(`visualGameAdmin.assetLibrary.talkingVideos.kind.${entry.asset_kind}`) }}</strong><Badge size="sm">{{ t(`visualGameAdmin.assetLibrary.talkingVideos.action.${entry.action}`) }}</Badge></div>
          <code>{{ auditMediaId(entry.before) }} → {{ auditMediaId(entry.after) }}</code>
          <span>{{ entry.actor_id }} · {{ formatDate(entry.created_at) }}</span>
        </article>
      </div>
    </Modal>

    <Modal
      :open="promptDetailOpen"
      :title="promptDetail === null ? '' : `${promptDetail.entity_name} · ${promptDetail.prompt.params.performance_state.display_name}`"
      width="980px"
      @update:open="promptDetailOpen = $event"
    >
      <div v-if="promptDetail !== null" class="tvt__prompt-detail">
        <section>
          <h3>{{ t('visualGameAdmin.assetLibrary.talkingVideos.generatedVideoPreview') }}</h3>
          <div v-if="promptDetail.video" class="tvt__detail-video">
            <video :src="promptDetail.video.public_url" controls preload="metadata" playsinline />
            <button type="button" class="tvt__asset-link" @click="openMediaAsset(promptDetail.video.media_asset_id)">
              <ExternalLink :size="13" />{{ promptDetail.video.media_asset_id }}
            </button>
          </div>
          <p v-else class="tvt__detail-video-empty">{{ t('visualGameAdmin.assetLibrary.talkingVideos.videoPendingHint') }}</p>
        </section>
        <section>
          <h3>{{ t('visualGameAdmin.assetLibrary.talkingVideos.selectionLogic') }}</h3>
          <p>{{ promptDetail.prompt.params.performance_state.design_goal }}</p>
          <ul>
            <li v-for="condition in promptDetail.prompt.params.performance_state.selection_conditions" :key="condition">{{ condition }}</li>
          </ul>
          <dl class="tvt__performance-spec">
            <div><dt>{{ t('visualGameAdmin.assetLibrary.talkingVideos.composition') }}</dt><dd>{{ promptDetail.prompt.params.performance_state.composition }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.assetLibrary.talkingVideos.facialPerformance') }}</dt><dd>{{ promptDetail.prompt.params.performance_state.facial_performance }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.assetLibrary.talkingVideos.mouthMotion') }}</dt><dd>{{ promptDetail.prompt.params.performance_state.mouth_motion }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.assetLibrary.talkingVideos.microAction') }}</dt><dd>{{ promptDetail.prompt.params.performance_state.micro_action }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.assetLibrary.talkingVideos.excludedEmotions') }}</dt><dd>{{ promptDetail.prompt.params.performance_state.excluded_emotions }}</dd></div>
          </dl>
        </section>
        <section>
          <h3>{{ t('visualGameAdmin.assetLibrary.talkingVideos.stateFrameAndPrompt') }}</h3>
          <div v-if="promptDetail.state_frame" class="tvt__state-frame-review">
            <button type="button" @click="openMediaAsset(promptDetail.state_frame.media_asset_id)">
              <img :src="promptDetail.state_frame.public_url" :alt="promptDetail.prompt.params.performance_state.display_name" />
              <span>{{ t('visualGameAdmin.assetLibrary.talkingVideos.openStateFrame') }} <ExternalLink :size="14" /></span>
            </button>
            <pre>{{ promptDetail.prompt.params.performance_state.state_frame_prompt }}</pre>
          </div>
          <pre v-else>{{ promptDetail.prompt.params.performance_state.state_frame_prompt }}</pre>
        </section>
        <section>
          <h3>{{ t('visualGameAdmin.assetLibrary.talkingVideos.h3Params') }}</h3>
          <pre>{{ JSON.stringify(promptDetail.prompt.params.h3, null, 2) }}</pre>
        </section>
        <section>
          <h3>{{ t('visualGameAdmin.assetLibrary.talkingVideos.referenceImages') }}</h3>
          <div class="tvt__detail-references">
            <figure v-for="reference in promptDetail.prompt.params.references" :key="reference.id">
              <img :src="reference.public_url" :alt="reference.role" />
              <figcaption>{{ reference.role }}</figcaption>
            </figure>
          </div>
        </section>
        <section>
          <h3>{{ t('visualGameAdmin.assetLibrary.talkingVideos.fullPrompt') }}</h3>
          <pre class="tvt__full-prompt">{{ promptDetail.prompt.text }}</pre>
        </section>
        <a
          class="tvt__call-link"
          :href="`/llm/calls?capability=embedding&request_id=${encodeURIComponent(promptDetail.prompt.embedding_call_id)}`"
          target="_blank"
          rel="noreferrer"
        >{{ t('visualGameAdmin.assetLibrary.talkingVideos.embeddingCall') }} <ExternalLink :size="14" /></a>
      </div>
      <template v-if="promptDetail !== null" #footer>
        <Button variant="ghost" @click="promptDetailOpen = false">{{ t('visualGameAdmin.common.cancel') }}</Button>
        <Button
          variant="secondary" :disabled="promptDetail.prompt.quality === 'rejected'"
          :loading="reviewingPromptId === promptDetail.prompt.id" @click="reviewPrompt(promptDetail, 'rejected')"
        >{{ t('visualGameAdmin.assetLibrary.talkingVideos.rejectPrompt') }}</Button>
        <Button
          variant="primary" :disabled="promptDetail.prompt.quality === 'approved'"
          :loading="reviewingPromptId === promptDetail.prompt.id" @click="reviewPrompt(promptDetail, 'approved')"
        >{{ t('visualGameAdmin.assetLibrary.talkingVideos.approvePrompt') }}</Button>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
.tvt { display: grid; gap: var(--drama-space-lg); }
.tvt__section-description { margin: 0 0 var(--drama-space-lg); color: var(--drama-color-text-muted); }
.tvt__spec-groups { display: grid; gap: var(--drama-space-xl); }
.tvt__spec-group { display: grid; gap: var(--drama-space-md); }
.tvt__spec-group-header { display: flex; align-items: center; justify-content: space-between; gap: var(--drama-space-md); padding-bottom: var(--drama-space-sm); border-bottom: 1px solid var(--drama-color-border); }
.tvt__spec-group-header h3 { margin: 0; color: var(--drama-color-text); font-size: var(--drama-font-size-lg); }
.tvt__spec-group-header span { display: block; margin-top: var(--drama-space-xs); color: var(--drama-color-text-muted); font-family: var(--drama-font-family-mono); font-size: var(--drama-font-size-xs); }
.tvt__identity-reference { display: grid; grid-template-columns: minmax(240px, 420px) minmax(0, 1fr); gap: var(--drama-space-md); align-items: center; padding: var(--drama-space-md); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-lg); background: var(--drama-color-surface-alt); }
.tvt__identity-reference button { padding: 0; overflow: hidden; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); cursor: pointer; }
.tvt__identity-reference img { display: block; width: 100%; aspect-ratio: 16 / 9; object-fit: cover; }
.tvt__identity-reference div { display: grid; gap: var(--drama-space-sm); }
.tvt__identity-reference strong { color: var(--drama-color-text); font-size: var(--drama-font-size-md); }
.tvt__identity-reference p { margin: 0; color: var(--drama-color-text-muted); line-height: var(--drama-line-height-normal); }
.tvt__identity-reference code { overflow: hidden; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); text-overflow: ellipsis; white-space: nowrap; }
.tvt__spec-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)); grid-template-rows: repeat(8, auto); gap: var(--drama-space-md); }
.tvt__spec-card { display: grid; grid-row: span 8; grid-template-rows: subgrid; gap: var(--drama-space-md); padding: var(--drama-space-lg); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-lg); background: var(--drama-color-surface); box-shadow: var(--drama-shadow-sm); }
.tvt__spec-card > p { margin: 0; color: var(--drama-color-text); line-height: var(--drama-line-height-normal); }
.tvt__spec-card-header { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--drama-space-md); }
.tvt__spec-card-header strong { display: block; font-size: var(--drama-font-size-md); }
.tvt__spec-card-header code { display: block; margin-top: var(--drama-space-xs); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.tvt__conditions { display: flex; flex-wrap: wrap; gap: var(--drama-space-xs); margin: 0; padding: 0; list-style: none; }
.tvt__conditions li { padding: var(--drama-space-xs) var(--drama-space-sm); border-radius: var(--drama-radius-full); background: var(--drama-color-surface-alt); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.tvt__phases { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-xs); }
.tvt__phases > span { margin-right: var(--drama-space-xs); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.tvt__scenario-filter { min-width: min(100%, 320px); }
.tvt__state-frame { position: relative; display: block; width: 100%; padding: 0; overflow: hidden; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); cursor: pointer; }
.tvt__state-frame img { display: block; width: 100%; aspect-ratio: 16 / 9; object-fit: cover; }
.tvt__state-frame span { position: absolute; right: var(--drama-space-sm); bottom: var(--drama-space-sm); display: inline-flex; align-items: center; gap: var(--drama-space-xs); padding: var(--drama-space-xs) var(--drama-space-sm); border-radius: var(--drama-radius-full); background: var(--drama-color-surface); color: var(--drama-color-text); box-shadow: var(--drama-shadow-sm); font-size: var(--drama-font-size-xs); }
.tvt__references { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-sm); }
.tvt__references figure, .tvt__detail-references figure { margin: 0; }
.tvt__references img { display: block; width: 100%; height: 112px; border-radius: var(--drama-radius-sm); object-fit: cover; background: var(--drama-color-surface-alt); }
.tvt__references figcaption { margin-top: var(--drama-space-xs); overflow: hidden; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); text-overflow: ellipsis; white-space: nowrap; }
.tvt__spec-video video { display: block; width: 100%; max-height: 220px; border-radius: var(--drama-radius-md); background: #000; }
.tvt__spec-video-empty { display: flex; align-items: center; justify-content: center; width: 100%; height: 96px; border: 1px dashed var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.tvt__spec-meta { display: flex; align-items: center; justify-content: space-between; gap: var(--drama-space-sm); color: var(--drama-color-text-muted); font-family: var(--drama-font-family-mono); font-size: var(--drama-font-size-xs); }
.tvt__spec-actions { display: flex; flex-wrap: wrap; gap: var(--drama-space-xs); padding-top: var(--drama-space-sm); border-top: 1px solid var(--drama-color-border); }
.tvt__prompt-detail { display: grid; gap: var(--drama-space-xl); }
.tvt__prompt-detail section { display: grid; gap: var(--drama-space-sm); }
.tvt__prompt-detail section + section { padding-top: var(--drama-space-md); border-top: 1px solid var(--drama-color-border); }
.tvt__prompt-detail h3, .tvt__prompt-detail p, .tvt__prompt-detail ul { margin: 0; }
.tvt__performance-spec { display: grid; gap: var(--drama-space-md); margin: var(--drama-space-sm) 0 0; }
.tvt__performance-spec div { display: grid; grid-template-columns: 132px minmax(0, 1fr); gap: var(--drama-space-md); }
.tvt__performance-spec dt { color: var(--drama-color-text-muted); font-weight: var(--drama-font-weight-medium); }
.tvt__performance-spec dd { margin: 0; color: var(--drama-color-text); line-height: var(--drama-line-height-normal); }
.tvt__prompt-detail pre { margin: 0; padding: var(--drama-space-md); overflow: auto; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface-alt); color: var(--drama-color-text); font-family: var(--drama-font-family-mono); font-size: var(--drama-font-size-sm); white-space: pre-wrap; }
.tvt__state-frame-review { display: grid; gap: var(--drama-space-lg); }
.tvt__state-frame-review button { max-width: 560px; padding: 0; overflow: hidden; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); color: var(--drama-color-primary); cursor: pointer; }
.tvt__state-frame-review img { display: block; width: 100%; aspect-ratio: 16 / 9; object-fit: cover; }
.tvt__state-frame-review span { display: inline-flex; align-items: center; gap: var(--drama-space-xs); padding: var(--drama-space-sm) var(--drama-space-md); }
.tvt__detail-video { display: grid; gap: var(--drama-space-sm); }
.tvt__detail-video video { display: block; width: 100%; max-height: 420px; border-radius: var(--drama-radius-md); background: #000; }
.tvt__detail-video-empty { margin: 0; padding: var(--drama-space-md); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface-alt); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.tvt__detail-references { display: grid; grid-template-columns: repeat(2, minmax(0, 280px)); gap: var(--drama-space-md); }
.tvt__detail-references img { display: block; width: 100%; aspect-ratio: 16 / 9; border-radius: var(--drama-radius-sm); object-fit: cover; background: var(--drama-color-surface-alt); }
.tvt__detail-references figcaption { margin-top: var(--drama-space-xs); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.tvt__full-prompt { max-height: 460px; }
.tvt__call-link { display: inline-flex; align-items: center; gap: var(--drama-space-xs); color: var(--drama-color-primary); }
.tvt__stack { display: grid; gap: var(--drama-space-xs); }
.tvt__stack span, .tvt__stack code { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.tvt__video { display: block; width: 200px; max-height: 120px; border-radius: var(--drama-radius-sm); background: var(--drama-color-surface-alt); }
.tvt__asset-link { display: flex; align-items: center; gap: var(--drama-space-xs); max-width: 200px; margin-top: var(--drama-space-xs); padding: 0; overflow: hidden; border: 0; background: transparent; color: var(--drama-color-text-muted); cursor: pointer; font-family: var(--drama-font-family-mono); font-size: var(--drama-font-size-xs); text-overflow: ellipsis; white-space: nowrap; }
.tvt__frames { display: flex; gap: var(--drama-space-sm); }
.tvt__frames button { padding: 0; overflow: hidden; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface-alt); cursor: pointer; }
.tvt__frames img { display: block; width: 72px; height: 72px; object-fit: cover; }
.tvt__actions { display: flex; justify-content: flex-end; gap: var(--drama-space-xs); }
.tvt__form { display: grid; gap: var(--drama-space-md); }
.tvt__history-identity { margin: 0 0 var(--drama-space-lg); color: var(--drama-color-text-muted); }
.tvt__history-list { display: grid; gap: var(--drama-space-sm); }
.tvt__history-item { display: grid; gap: var(--drama-space-xs); padding: var(--drama-space-md); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); }
.tvt__history-item div { display: flex; align-items: center; gap: var(--drama-space-sm); }
.tvt__history-item code, .tvt__history-item span { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
@media (max-width: 760px) {
  .tvt__identity-reference { grid-template-columns: 1fr; }
  .tvt__spec-grid { grid-template-columns: 1fr; }
  .tvt__detail-references { grid-template-columns: 1fr; }
}
</style>
